import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReturnConsignmentsListStore } from 'zustand-store';
import { useReturnDetails } from 'pages/exchange-and-return/hooks';
import { Text } from 'components';
import { FontSize, FontWeight, outletRoutes, routes } from 'commons';
import { DashboardConsignment, Loader, ReturnConsignment } from '../components';
import * as S from '../styles';

export function ReturnConsignmentList() {
  const navigate = useNavigate();
  const {
    isValidating,
    mutate,
    returnSearch,
    returnFilter,
    returnConsignments,
    consignmentsToReturn,
    mutateConsignmentsToReturn,
    isValidatingConsignmentsToReturn,
  } = ReturnConsignmentsListStore.useGetReturnConsignments();
  const { selectedCard, setSelectedCard, returnDetails, mutateReturnDetails } =
    useReturnDetails();

  const { exchangeAndReturn } = routes;

  function clearDetails() {
    setSelectedCard(null);

    navigate(exchangeAndReturn.path);
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      mutate();
    }, 100);
    mutateConsignmentsToReturn();

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnSearch, returnFilter]);

  useEffect(() => {
    if (
      selectedCard?.type === 'toReturn' &&
      (!consignmentsToReturn ||
        consignmentsToReturn?.every(
          ({ consignmentCode }) =>
            consignmentCode !== selectedCard?.consignmentCode
        ))
    ) {
      toast.warn(
        `Pedido de código ${selectedCard.consignmentCode} não está mais disponível`
      );
      clearDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consignmentsToReturn]);

  useEffect(() => {
    const currentCard = returnConsignments?.find(
      ({ protocol }) => protocol === selectedCard?.protocol
    );

    if (currentCard && selectedCard) {
      if (
        returnDetails?.status !== currentCard.returnRequestStatus &&
        currentCard.returnRequestStatus !== 'CREATED'
      ) {
        mutateReturnDetails();
      }
    } else if (selectedCard?.type === 'return') {
      toast.warn(
        `Pedido de protocolo ${selectedCard?.protocol} não está mais disponível`
      );
      clearDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnConsignments]);

  async function requestDetails({
    protocol,
    consignmentCode,
  }: {
    protocol: string;
    consignmentCode: string;
  }) {
    setSelectedCard({ type: 'return', consignmentCode, protocol });

    const { exchangeAndReturn } = routes;
    const { details } = outletRoutes;

    navigate(`${exchangeAndReturn.path}/${details.path}`);
  }

  async function requestReturn(consignmentCode: string) {
    setSelectedCard({
      type: 'toReturn',
      consignmentCode,
      protocol: '',
    });

    const { selectReturn } = outletRoutes;
    navigate(`${selectReturn.path}`);
  }

  if (isValidating || isValidatingConsignmentsToReturn) {
    return (
      <S.Wrapper>
        <Loader />
      </S.Wrapper>
    );
  }

  const renderConsignmentsToReturn = consignmentsToReturn?.map(
    (consignment) => {
      const { consignmentCode } = consignment;
      const isSelected =
        consignmentCode === selectedCard?.consignmentCode &&
        !selectedCard?.protocol;

      return (
        <DashboardConsignment
          key={`${consignmentCode}`}
          consignment={consignment}
          isSelected={isSelected}
          action={isSelected ? undefined : () => requestReturn(consignmentCode)}
        />
      );
    }
  );

  const renderReturnConsignments = returnConsignments?.map((consignment) => {
    const { consignmentCode, protocol } = consignment;
    const isSelected = protocol === selectedCard?.protocol;

    return (
      <ReturnConsignment
        key={`${consignmentCode}-${protocol}`}
        consignment={consignment}
        isSelected={isSelected}
        action={
          isSelected
            ? undefined
            : () =>
                requestDetails({
                  protocol,
                  consignmentCode,
                })
        }
      />
    );
  });

  const hasConsignmentsToReturn = Boolean(consignmentsToReturn?.length);
  const hasReturnConsignments = Boolean(returnConsignments?.length);

  return (
    <S.Wrapper>
      {renderConsignmentsToReturn}

      {hasConsignmentsToReturn && hasReturnConsignments && (
        <S.TextWrapper $start>
          <Text size={FontSize.font16} weight={FontWeight.bold}>
            Em andamento / Finalizada
          </Text>
        </S.TextWrapper>
      )}

      {hasReturnConsignments && renderReturnConsignments}

      {!hasConsignmentsToReturn && !hasReturnConsignments && (
        <S.TextWrapper>
          <Text size={FontSize.font16} weight={FontWeight.medium}>
            Nenhuma devolução encontrada
          </Text>
        </S.TextWrapper>
      )}
    </S.Wrapper>
  );
}
